import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { adminApi, AdminApiError } from '../adminApi.js'

export interface DailyRow {
  date: string
  passes_installed: number
  stamps_issued: number
  rewards_redeemed: number
  cycle_completions: number
  broadcasts_sent: number
  active_customers: number
}

export type LeaderboardMetric =
  | 'stamps_issued'
  | 'passes_installed'
  | 'rewards_redeemed'
  | 'cycle_completions'
  | 'broadcasts_sent'

export interface LastActivity {
  last_stamp_at: string | null
  last_broadcast_at: string | null
}

export interface TenantAnalyticsResponse {
  from: string
  to: string
  daily: DailyRow[]
  totals: Omit<DailyRow, 'date'>
  previous_totals: Omit<DailyRow, 'date'>
  last_activity: LastActivity
  last_refreshed_at: string | null
}

export interface GlobalAnalyticsResponse {
  from: string
  to: string
  daily: DailyRow[]
  totals: Omit<DailyRow, 'date'>
  previous_totals: Omit<DailyRow, 'date'>
  leaderboard_metric: LeaderboardMetric
  leaderboard: Array<{
    tenant_id: string
    tenant_name: string
    value: number
  }>
  quiet_tenants: Array<{ tenant_id: string; tenant_name: string }>
  last_refreshed_at: string | null
}

export const tenantAnalyticsKey = (id: string, from: string, to: string) =>
  ['admin', 'analytics', 'tenant', id, from, to] as const
export const globalAnalyticsKey = (
  from: string,
  to: string,
  metric: LeaderboardMetric,
) => ['admin', 'analytics', 'global', from, to, metric] as const

export function useTenantAnalytics(id: string, from: string, to: string) {
  return useQuery({
    queryKey: tenantAnalyticsKey(id, from, to),
    queryFn: () =>
      adminApi.get<TenantAnalyticsResponse>(
        `/analytics/tenants/${id}?from=${from}&to=${to}`,
      ),
    enabled: Boolean(id && from && to),
  })
}

export function useGlobalAnalytics(
  from: string,
  to: string,
  metric: LeaderboardMetric,
) {
  return useQuery({
    queryKey: globalAnalyticsKey(from, to, metric),
    queryFn: () =>
      adminApi.get<GlobalAnalyticsResponse>(
        `/analytics/global?from=${from}&to=${to}&leaderboard_metric=${metric}`,
      ),
    enabled: Boolean(from && to),
  })
}

interface RefreshResponse {
  date: string
  tenants_processed: number
}

export function useRefreshAnalytics() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => adminApi.post<RefreshResponse>('/analytics/refresh'),
    onSuccess: (res) => {
      // Wipe every analytics cache key — both global and per-tenant — so the
      // next render shows the freshly-written rollup.
      qc.invalidateQueries({ queryKey: ['admin', 'analytics'] })
      toast.success(
        `Refreshed ${res.tenants_processed} tenant${
          res.tenants_processed === 1 ? '' : 's'
        } for ${res.date}.`,
      )
    },
    onError: (err) =>
      toast.error(
        err instanceof AdminApiError
          ? err.userMessage
          : 'Could not refresh analytics.',
      ),
  })
}
